"use client"

import type React from "react"

import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Plus, Search, Users, X, Briefcase, ArrowLeft, Building2 } from "lucide-react"
import { FIELD_LABEL, FIELD_UNDERLINE } from "@/components/graphite/field"
import { cn } from "@/lib/utils"

/**
 * A field label in this wizard.
 *
 * `FieldShell` would be the component, but its children are the control and
 * this file's controls sit in grids their labels are not inside. Same
 * `FIELD_LABEL` token, so the two cannot drift.
 */
const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")
const AREA = cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")
const TRIGGER = cn(CONTROL, "h-auto")

function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <div className={cn(FIELD_LABEL, "mb-[9px]")}>
      {label}
      {required && <span className="ml-0.5 text-destructive">*</span>}
    </div>
  )
}

/** The wizard's three steps, named so the indicator can say more than "2". */
const STEPS = ["Search & details", "Hiring managers", "Job details"] as const
import { CompanyAutocomplete } from "@/components/community/company-autocomplete"
import { CreateCompanyForm } from "@/components/community/create-company-form"
//import { useCompany } from "@/hooks/useCompany"
import { toast } from "sonner"
import { useGetSavedSearches } from "@/hooks/use-get-saved-search"
import { useNavigate, useSearch } from "@tanstack/react-router"

export interface SavedSearch {
  id: string
  name: string
  query: string
  filters: string[]
  createdAt: string
}

export interface HiringManager {
  id: string
  contact_id?: string
  name: string
  email: string
  position: string
  phone?: string
  contact_type?: string
  source?: string
}

export interface JobConfigurationData {
  title: string
  department: string
  location: string
  type: "full_time" | "part_time" | "contract" | "intern"
  salary_range: string
  description: string
  selectedSearch: SavedSearch | null
  company: string
  company_id: string | undefined,
  hiringManagers: HiringManager[]
}

interface JobConfigurationDialogProps {
  trigger?: React.ReactNode
  onJobCreated?: (job: JobConfigurationData) => void | Promise<void>
  onJobUpdated?: (jobId: string, job: JobConfigurationData) => void | Promise<void>
  jobId?: string
  defaultValues?: Partial<JobConfigurationData>
  mode?: "create" | "edit"
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const baseJobData: JobConfigurationData = {
  title: "",
  department: "",
  location: "",
  type: "full_time",
  salary_range: "",
  description: "",
  selectedSearch: null,
  company: "",
  company_id: undefined as string | undefined,
  hiringManagers: [],
}

const buildJobData = (overrides?: Partial<JobConfigurationData>): JobConfigurationData => ({
  ...baseJobData,
  ...overrides,
  selectedSearch: overrides?.selectedSearch ?? baseJobData.selectedSearch,
  company_id: overrides?.company_id ?? baseJobData.company_id,
  hiringManagers: overrides?.hiringManagers ?? baseJobData.hiringManagers,
})

export function JobConfigurationDialog({
  onJobCreated,
  onJobUpdated,
  jobId,
  defaultValues,
  mode = "create",
  open,
  onOpenChange,
  trigger,
}: JobConfigurationDialogProps) {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { createJob?: boolean | string }
  const [currentStep, setCurrentStep] = useState(1)
  const [localOpen, setLocalOpen] = useState(false)
  //const { companies, addCompany } = useCompany()
  const {savedSearches}=useGetSavedSearches()

  /*
    Route-controlled unless a caller is controlling it explicitly. This used to
    also require `!trigger`, which quietly broke the command palette: "Create
    New Job" navigates to `/jobs?createJob=true`, the Jobs page passes a trigger
    button, and so the search parameter was ignored and the palette only ever
    landed you on the page. A trigger is a way *in*, not a claim to own the
    open state — `handleDialogOpenChange` routes it either way.
  */
  const usesRouterOpen = mode === "create" && open === undefined && onOpenChange === undefined
  const isOpen = usesRouterOpen ? !!search.createJob : (open ?? localOpen)
  const handleDialogOpenChange = (nextOpen: boolean) => {
    /*
      Disabling Create Job and Back left one way out: the dialog's own close —
      the ×, Escape and the overlay all land here. Taken during a slow POST it
      dismissed the wizard while the request carried on, and when that request
      finally landed it reset and closed whatever form had been opened since.
      Nothing dismisses a submission in flight; it is over in a moment either
      way, and the button beside it says "Saving…".
    */
    if (!nextOpen && isSubmittingRef.current) return
    if (usesRouterOpen) {
      return nextOpen ? openCreateJob() : closeCreateJob()
    }
    if (onOpenChange) {
      onOpenChange(nextOpen)
      return
    }
    setLocalOpen(nextOpen)
  }


  /*
    Whether a submission is in flight. Awaiting the callback fixed a form being
    thrown away on failure and introduced this: the dialog stayed open with its
    button live, so a second click before the first POST settled created the job
    twice.
  */
  const [isSubmitting, setIsSubmitting] = useState(false)
  /*
    The same flag, readable from `handleDialogOpenChange` above — which is
    defined before the state it needs, and is a plain function rather than a
    hook, so a ref rather than a reorder.
  */
  const isSubmittingRef = useRef(false)
  const setSubmitting = (next: boolean) => {
    isSubmittingRef.current = next
    setIsSubmitting(next)
  }
  const [isCreatingCompany, setIsCreatingCompany] = useState(false)
  const [newCompanySeedName, setNewCompanySeedName] = useState("")

  const [jobData, setJobData] = useState<JobConfigurationData>(() => buildJobData(defaultValues))
  const defaultValuesRef = useRef(defaultValues)
  useEffect(() => {
    defaultValuesRef.current = defaultValues
  }, [defaultValues])

  const [newHiringManager, setNewHiringManager] = useState({
    name: "",
    email: "",
    position: "",
  })


  const handleAddHiringManager = () => {
    if (newHiringManager.name && newHiringManager.email) {
      setJobData((prev) => ({
        ...prev,
        hiringManagers: [
          ...prev.hiringManagers,
          {
            id: Date.now().toString(),
            ...newHiringManager,
          },
        ],
      }))
      setNewHiringManager({ name: "", email: "", position: "" })
    }
  }

  const handleRemoveHiringManager = (id: string) => {
    setJobData((prev) => ({
      ...prev,
      hiringManagers: prev.hiringManagers.filter((hm) => hm.id !== id),
    }))
  }

  const handleSelectSearch = (search: SavedSearch) => {
    setJobData((prev) => ({ ...prev, selectedSearch: search }))
  }

  const handleCreateCompany = (companyName: string) => {
    setIsCreatingCompany(true)
    setNewCompanySeedName(companyName)
  }

  const handleCancelCompanyCreation = () => {
    setIsCreatingCompany(false)
    setNewCompanySeedName("")
  }

  const closeCreateJob = () => {
    navigate({
      to: "/jobs",
      search: (prev:any) => ({ ...prev, createJob: undefined }),
      replace: true,
    })
  }

  const openCreateJob= () => {
    navigate({
      to: "/jobs",
      search: (prev:any) => ({ ...prev, createJob: true }),
      replace: true,
    })
  }

  const handleSubmit = async () => {
    // The ref, not the state: two clicks landing before React re-renders both
    // read the old `false`, and the disabled attribute is not on the button
    // until that render either.
    if (isSubmittingRef.current) return
    setSubmitting(true)
    /*
      Await it. This used to fire the callback and immediately reset every field
      and close, so a slow or rejected `POST /jobs` threw away a completed form
      with no way back to it but retyping. Nothing is cleared unless the job
      actually exists.
    */
    try {
      if (mode === "edit" && onJobUpdated && jobId) {
        await onJobUpdated(jobId, jobData)
      } else {
        await onJobCreated?.(jobData)
      }
    } catch {
      // The caller has already said what went wrong; keep the form intact.
      setSubmitting(false)
      return
    }
    setSubmitting(false)
    setJobData(buildJobData(defaultValues))
    setCurrentStep(1)
    setIsCreatingCompany(false)
    setNewCompanySeedName("")
    if (usesRouterOpen) {
      closeCreateJob()
    } else {
      setLocalOpen(false)
      onOpenChange?.(false)
    }
  }


  //const canProceedToStep2 = jobData.selectedSearch && jobData.title && jobData.company_id
  const canProceedToStep2 = jobData.title && jobData.company_id
  //  const canProceedToStep3 = canProceedToStep2 && jobData.hiringManagers.length > 0
  const canProceedToStep3 = canProceedToStep2
  const canSubmit = canProceedToStep3 && jobData.department && jobData.location

  useEffect(() => {
    if (!isOpen) return
    // Apply defaults once per open so in-progress edits are not overwritten by parent re-renders.
    setJobData(buildJobData(defaultValuesRef.current))
    setCurrentStep(1)
    setIsCreatingCompany(false)
    setNewCompanySeedName("")
  }, [isOpen])

  const dialogTitle = mode === "edit" ? "Edit Job" : "Configure New Job"
  const submitLabel = mode === "edit" ? "Save Changes" : "Create Job"

  return (
    <>
      {/*
        A caller's trigger wins. Dropping `!trigger` from `usesRouterOpen` fixed
        the command palette and broke this: the Jobs page hands in the flat
        "+ New job" the header is drawn with, and the branch below rendered the
        built-in button over it instead — a stock filled control in a row of
        text actions. Which control is shown and who owns the open state are two
        separate questions; `handleDialogOpenChange` still routes a trigger's
        open through the URL when nobody else is controlling it.
      */}
      {trigger ? (
        <span onClick={() => handleDialogOpenChange(true)}>{trigger}</span>
      ) : usesRouterOpen ? (
        <PageAction tone="primary" data-testid="configure-job-btn" onClick={openCreateJob}>
          <Plus className="size-[15px]" />
          Configure New Job
        </PageAction>
      ) : null}

      <DialogShell
        open={isOpen}
        onOpenChange={handleDialogOpenChange}
        wide
        title={dialogTitle}
        /*
          One footer for a wizard of three steps, rather than an action row
          buried at the bottom of each one. Where the buttons live should not
          depend on which step you are on.
        */
        footer={
          <>
            {currentStep > 1 && (
              <QuietAction
                size="md"
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={isSubmitting}
                className="@sm:mr-auto"
              >
                Back
              </QuietAction>
            )}
            {currentStep === 1 && (
              <PageAction
                tone="primary"
                onClick={() => setCurrentStep(2)}
                disabled={!canProceedToStep2}
              >
                Next: Add Hiring Managers
              </PageAction>
            )}
            {currentStep === 2 && (
              <PageAction
                tone="primary"
                onClick={() => setCurrentStep(3)}
                disabled={!canProceedToStep3}
              >
                Next: Job Details
              </PageAction>
            )}
            {currentStep === 3 && (
              <PageAction
                tone="primary"
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? "Saving…" : submitLabel}
              </PageAction>
            )}
          </>
        }
      >
        <div>
          {/*
            Where you are, in words as well as numbers. Three bare circles said
            "3" without saying what 3 was, so the wizard's own step names now
            carry it, and the rule under the current one is the marker rather
            than a filled disc.
          */}
          <ol className="mb-5 flex items-baseline gap-5 border-b">
            {STEPS.map((name, index) => {
              const step = index + 1
              const active = currentStep === step
              return (
                <li key={name}>
                  <span
                    aria-current={active ? "step" : undefined}
                    className={cn(
                      "-mb-px inline-flex items-baseline gap-1.5 border-b-2 pb-2 text-[12px] transition-colors",
                      active
                        ? "border-primary font-medium text-foreground"
                        : currentStep > step
                          ? "border-transparent text-muted-foreground"
                          : "border-transparent text-muted-foreground/60",
                    )}
                  >
                    <span className="font-mono text-[10.5px]">{step}</span>
                    {name}
                  </span>
                </li>
              )
            })}
          </ol>

          {/* Step 1: Search & Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <section>
                <h2 className="flex items-center gap-2 text-[12.5px] font-medium">
                  <Search className="size-4 text-muted-foreground" />
                  Select Search & Job Details
                </h2>
                <div className="mt-4 space-y-5">
                  {/* Search Selection */}
                  <div>
                    <div className={FIELD_LABEL}>Link a saved search — optional</div>
                    {savedSearches.length === 0 ? (
                      /* An empty list drew as two hairlines with nothing
                         between them. It says what it is instead. */
                      <p className="mt-[9px] border-b pb-[9px] text-[12px] text-muted-foreground">
                        No saved searches yet — the job can be linked to one later.
                      </p>
                    ) : (
                    <div className="mt-[9px] divide-y border-y">
                      {savedSearches.map((search) => {
                        const chosen = jobData.selectedSearch?.id === search.id
                        return (
                          <button
                            key={search.id}
                            type="button"
                            aria-pressed={chosen}
                            onClick={() => handleSelectSearch(search)}
                            className={cn(
                              "flex w-full items-start justify-between gap-3 py-2.5 text-left transition-colors",
                              chosen ? "bg-primary/[0.06]" : "hover:bg-foreground/[0.03]",
                            )}
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-[12.5px] font-medium leading-[1.3]">
                                {search.name}
                              </span>
                              <span className="mt-1 block truncate text-[11.5px] leading-[1.4] text-muted-foreground">
                                {search.query}
                              </span>
                              {search.filters.length > 0 && (
                                <span className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                                  {search.filters.map((filter, index) => (
                                    <span
                                      key={index}
                                      className="font-mono text-[10.5px] text-muted-foreground"
                                    >
                                      {filter}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </span>
                            <span className="flex-none font-mono text-[10.5px] text-muted-foreground">
                              {search.createdAt}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                    )}
                  </div>

                  <Separator />

                  {/* Basic Job Info */}
                  <div className="grid grid-cols-1 gap-5 @sm:grid-cols-2">
                    <div>
                      <FieldLabel label="Job Title" required={true} />
                      <input
                        id="jobTitle"
                        aria-label="Job Title"
                        value={jobData.title}
                        onChange={(e) => setJobData((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., Senior Python Developer"
                        className={CONTROL}
                      />
                    </div>
                    <div>
                      <FieldLabel label="Job Type" required={false} />
                      <Select
                        value={jobData.type}
                        onValueChange={(value: any) => setJobData((prev) => ({ ...prev, type: value }))}
                      >
                        <SelectTrigger aria-label="Job type" className={TRIGGER}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Full-time</SelectItem>
                          <SelectItem value="part_time">Part-time</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="intern">Internship</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <FieldLabel label="Company" required={true} />

                    {!isCreatingCompany ? (
                      <CompanyAutocomplete
                        value={jobData.company}
                        onChange={(name, id) => setJobData((prev) => ({ ...prev, company: name,company_id: id, }))}
                        onCreateCompany={handleCreateCompany}
                        placeholder="Start typing company name..."
                        /* It already forwards a class to its input, so the
                           wizard can dress it without changing the component
                           four other forms use in their own styles. */
                        className={CONTROL}
                      />
                    ) : (
                      <div className="space-y-4 mt-4 p-4 border rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Create New Company
                          </h4>
                          <Button type="button" variant="ghost" size="sm" onClick={handleCancelCompanyCreation}>
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back to Search
                          </Button>
                        </div>
                        <CreateCompanyForm
                          initialName={newCompanySeedName}
                          onCancel={handleCancelCompanyCreation}
                          onCreated={(createdCompany) => {
                            setJobData((prev) => ({ ...prev, company: createdCompany.name, company_id: createdCompany.id }))
                            setIsCreatingCompany(false)
                            setNewCompanySeedName("")
                            toast.success("New company created successfully.")
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </section>

            </div>
          )}

          {/* Step 2: Hiring Managers */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <section>
                <h2 className="flex items-center gap-2 text-[12.5px] font-medium">
                  <Users className="size-4 text-muted-foreground" />
                  Hiring Managers & Contacts
                </h2>
                <div className="mt-4 space-y-5">
                  {jobData.hiringManagers.length > 0 && (
                    <div>
                      <div className={FIELD_LABEL}>Current hiring managers</div>
                      <div className="mt-[9px] divide-y border-y">
                        {jobData.hiringManagers.map((hm) => (
                          <div key={hm.id} className="flex items-center gap-2 py-2.5">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[12.5px] font-medium leading-[1.3]">
                                {hm.name}
                              </p>
                              <p className="truncate text-[11.5px] leading-[1.4] text-muted-foreground">
                                {[hm.position, hm.email].filter(Boolean).join(" · ")}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveHiringManager(hm.id)}
                              aria-label={`Remove ${hm.name}`}
                              className="flex size-8 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-destructive"
                            >
                              <X className="size-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <FieldLabel label="Add Hiring Manager" required={false} />
                    <div className="mt-[9px] grid grid-cols-1 gap-4 @sm:grid-cols-3">
                      <input
                        placeholder="Full Name *"
                        aria-label="Hiring manager name"
                        value={newHiringManager.name}
                        onChange={(e) => setNewHiringManager((prev) => ({ ...prev, name: e.target.value }))}
                        className={CONTROL}
                      />
                      <input
                        placeholder="Email *"
                        type="email"
                        aria-label="Hiring manager email"
                        value={newHiringManager.email}
                        onChange={(e) => setNewHiringManager((prev) => ({ ...prev, email: e.target.value }))}
                        className={CONTROL}
                      />
                      <div className="flex gap-2">
                        <input
                          placeholder="Position"
                          aria-label="Hiring manager position"
                          className={CONTROL}
                          value={newHiringManager.position}
                          onChange={(e) => setNewHiringManager((prev) => ({ ...prev, position: e.target.value }))}
                        />
                        <Button onClick={handleAddHiringManager} size="sm">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          )}

          {/* Step 3: Additional Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <section>
                <h2 className="flex items-center gap-2 text-[12.5px] font-medium">
                  <Briefcase className="size-4 text-muted-foreground" />
                  Job Details
                </h2>
                <div className="mt-4 space-y-5">
                  <div className="grid grid-cols-1 gap-5 @sm:grid-cols-2">
                    <div>
                      <FieldLabel label="Department" required={true} />
                      <input
                        id="department"
                        aria-label="Department"
                        value={jobData.department}
                        onChange={(e) => setJobData((prev) => ({ ...prev, department: e.target.value }))}
                        placeholder="e.g., Engineering"
                        className={CONTROL}
                      />
                    </div>
                    <div>
                      <FieldLabel label="Location" required={true} />
                      <input
                        id="location"
                        aria-label="Location"
                        value={jobData.location}
                        onChange={(e) => setJobData((prev) => ({ ...prev, location: e.target.value }))}
                        placeholder="e.g., San Francisco, CA"
                        className={CONTROL}
                      />
                    </div>
                  </div>

                  <div>
                    <FieldLabel label="Salary Range" required={false} />
                    <input
                      id="salary_range"
                      aria-label="Salary Range"
                      value={jobData.salary_range}
                      onChange={(e) => setJobData((prev) => ({ ...prev, salary_range: e.target.value }))}
                      placeholder="e.g., $120,000 - $180,000"
                      className={CONTROL}
                    />
                  </div>

                  <div>
                    <FieldLabel label="Job Description" required={false} />
                    <textarea
                      id="description"
                      value={jobData.description}
                      onChange={(e) => setJobData((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Detailed job description..."
                      rows={6}
                      aria-label="Job description"
                      className={AREA}
                    />
                  </div>

                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-3">Job Configuration Summary</h4>
                    <div className="space-y-2 text-sm">
                      <p>
                        <strong>Search:</strong> {jobData.selectedSearch?.name}
                      </p>
                      <p>
                        <strong>Title:</strong> {jobData.title}
                      </p>
                      <p>
                        <strong>Company:</strong> {jobData.company}
                      </p>
                      <p>
                        <strong>Hiring Managers:</strong> {jobData.hiringManagers.length} added
                      </p>
                      <p>
                        <strong>Department:</strong> {jobData.department}
                      </p>
                      <p>
                        <strong>Location:</strong> {jobData.location}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          )}
        </div>
      </DialogShell>
    </>
  )
}
